import { User } from '../types/User';
import { userService, UserPreferences } from '../services/userService';
import { useAuthStore } from '../store/authStore';
import { logger } from '../utils/logger';

/**
 * Service de synchronisation entre les stores Zustand et l'API
 * Gère la cohérence des données utilisateur dans l'application
 */
class UserSyncService {
  private static instance: UserSyncService;
  private syncQueue: Map<string, Promise<unknown>> = new Map();
  private cache: Map<string, { data: unknown; timestamp: number; ttl: number }> = new Map();

  private constructor() {}

  static getInstance(): UserSyncService {
    if (!UserSyncService.instance) {
      UserSyncService.instance = new UserSyncService();
    }
    return UserSyncService.instance;
  }

  /**
   * Synchronise le profil utilisateur avec l'API et met à jour les stores
   */
  async syncUserProfile(userId: string | number, forceRefresh = false): Promise<User> {
    const cacheKey = `user-profile-${userId}`;

    // Vérifier le cache si pas de refresh forcé
    if (!forceRefresh) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        logger.debug('User profile from cache:', userId);
        return cached as User;
      }
    }

    // Éviter les appels simultanés
    if (this.syncQueue.has(cacheKey)) {
      logger.debug('User profile sync already in progress:', userId);
      return this.syncQueue.get(cacheKey)! as Promise<User>;
    }

    const syncPromise = this.performUserProfileSync(userId);
    this.syncQueue.set(cacheKey, syncPromise);

    try {
      const user = await syncPromise;
      this.setCached(cacheKey, user, 5 * 60 * 1000); // 5 minutes TTL
      return user;
    } finally {
      this.syncQueue.delete(cacheKey);
    }
  }

  /**
   * Met à jour le profil utilisateur avec optimistic update
   */
  async updateUserProfile(updates: Partial<User>): Promise<User> {
    const authStore = useAuthStore.getState();
    const currentUser = authStore.user;

    if (!currentUser) {
      throw new Error('Utilisateur non authentifié');
    }

    // Optimistic update - mettre à jour le store immédiatement
    authStore.patchUser(updates);

    logger.info('Optimistic update applied:', updates);

    // Filtrer les champs pour ne garder que ceux acceptés par le backend
    // (schéma userProfileUpdateSchema dans validation.ts)
    const ALLOWED_FIELDS = [
      'name', 'phoneNumber', 'residence', 'speciality', 'licenseNumber',
      'phone', 'city', 'specialty', 'registrationNumber',
      'gender', 'bloodType', 'dateOfBirth',
      'emergencyContact', 'medicalHistory', 'allergies', 'preferences',
      'countryCode', 'dialCode',
    ] as const;

    const filteredUpdates: Record<string, unknown> = {};
    for (const key of ALLOWED_FIELDS) {
      if (key in updates && (updates as Record<string, unknown>)[key] !== undefined) {
        filteredUpdates[key] = (updates as Record<string, unknown>)[key];
      }
    }

    // Si aucun champ modifiable, pas besoin d'appeler l'API
    if (Object.keys(filteredUpdates).length === 0) {
      logger.debug('No modifiable fields to send to API, skipping PATCH');
      return currentUser;
    }

    try {
      // Appel API avec uniquement les champs autorisés
      const updatedUser = await userService.updateCurrentUserProfile(filteredUpdates);

      // Sync avec le store (au cas où l'API retourne des données différentes)
      authStore.patchUser(updatedUser);

      // Invalider le cache
      this.invalidateUserCache(currentUser.id);

      logger.info('User profile updated successfully:', currentUser.id);
      return updatedUser;

    } catch (error) {
      // Rollback en cas d'erreur
      authStore.patchUser(currentUser);
      logger.error('User profile update failed, rollback applied:', error);
      throw error;
    }
  }

  /**
   * Synchronise les préférences utilisateur
   */
  async syncUserPreferences(userId: string | number, forceRefresh = false): Promise<UserPreferences> {
    const cacheKey = `user-preferences-${userId}`;

    if (!forceRefresh) {
      const cached = this.getCached(cacheKey);
      if (cached) {
        return cached as UserPreferences;
      }
    }

    if (this.syncQueue.has(cacheKey)) {
      return this.syncQueue.get(cacheKey)! as Promise<UserPreferences>;
    }

    const syncPromise = userService.getUserPreferences(userId);
    this.syncQueue.set(cacheKey, syncPromise);

    try {
      const preferences = await syncPromise;
      this.setCached(cacheKey, preferences, 10 * 60 * 1000); // 10 minutes TTL
      return preferences;
    } finally {
      this.syncQueue.delete(cacheKey);
    }
  }

  /**
   * Met à jour les préférences utilisateur avec optimistic update
   */
  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    const authStore = useAuthStore.getState();
    const currentUser = authStore.user;

    if (!currentUser) {
      throw new Error('Utilisateur non authentifié');
    }

    // Note: Les préférences ne sont pas dans authStore, donc pas d'optimistic update possible
    // On pourrait ajouter un store séparé pour les préférences si nécessaire

    try {
      const updatedPreferences = await userService.updateCurrentUserPreferences(preferences);

      // Invalider le cache
      this.invalidateUserCache(currentUser.id);

      logger.info('User preferences updated successfully:', currentUser.id);
      return updatedPreferences;

    } catch (error) {
      logger.error('User preferences update failed:', error);
      throw error;
    }
  }

  /**
   * Synchronise automatiquement après login
   */
  async syncAfterLogin(user: User): Promise<void> {
    try {
      // Initialiser immédiatement le service utilisateur pour éviter "No current user"
      userService.setCurrentUser(user);

      // Sync du profil complet depuis l'API
      const fullProfile = await this.syncUserProfile(user.id, true);

      // Mettre à jour le store avec les données les plus récentes de manière atomique
      // On utilise patchUser pour éviter de redéclencher une boucle de sync via updateUser/updateUserProfile
      const authStore = useAuthStore.getState();
      authStore.patchUser(fullProfile);

      logger.info('Post-login sync completed:', user.id);

    } catch (error) {
      logger.error('Post-login sync failed:', error);
      // Ne pas throw pour ne pas bloquer le login
    }
  }

  /**
   * Invalide tout le cache d'un utilisateur
   */
  invalidateUserCache(userId: string | number): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key =>
      key.includes(`user-${userId}`)
    );

    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });

    logger.debug('User cache invalidated:', userId);
  }

  /**
   * Nettoie le cache expiré
   */
  cleanupExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((value, key) => {
      if (now > value.timestamp + value.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });

    if (keysToDelete.length > 0) {
      logger.debug('Expired cache cleaned up:', keysToDelete.length, 'entries');
    }
  }

  /**
   * Hook pour écouter les changements du store et synchroniser avec l'API
   */
  setupStoreSync(): void {
    // S'abonner aux changements du store d'authentification
    // Cela permet à UnifiedUserService d'avoir toujours l'utilisateur courant
    // même après un rechargement de page (hydratation de Zustand)
    useAuthStore.subscribe(
      (state) => state.user,
      (user: User | null) => {
        if (user) {
          userService.setCurrentUser(user);
        } else {
          userService.cleanup();
        }
      },
      { fireImmediately: true }
    );

    // Nettoyer le cache toutes les 5 minutes
    setInterval(() => {
      this.cleanupExpiredCache();
    }, 5 * 60 * 1000);

    logger.debug('User sync service initialized and store subscription active');
  }

  // Méthodes privées

  private async performUserProfileSync(userId: string | number): Promise<User> {
    logger.debug('Fetching user profile from API:', userId);

    // Appeler l'API pour récupérer le profil complet
    const user = await userService.getUserById(userId);

    if (!user) {
      throw new Error(`Utilisateur ${userId} non trouvé`);
    }

    return user;
  }

  private getCached(key: string): unknown | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.timestamp + cached.ttl) {
      return cached.data;
    }

    // Supprimer le cache expiré
    if (cached) {
      this.cache.delete(key);
    }

    return null;
  }

  private setCached(key: string, data: unknown, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }
}

// Hook React pour utiliser le service de sync
export const useUserSync = () => {
  const syncService = UserSyncService.getInstance();

  return {
    syncUserProfile: (userId: string | number, forceRefresh = false) =>
      syncService.syncUserProfile(userId, forceRefresh),

    updateUserProfile: (updates: Partial<User>) =>
      syncService.updateUserProfile(updates),

    syncUserPreferences: (userId: string | number, forceRefresh = false) =>
      syncService.syncUserPreferences(userId, forceRefresh),

    updateUserPreferences: (preferences: Partial<UserPreferences>) =>
      syncService.updateUserPreferences(preferences),

    syncAfterLogin: (user: User) =>
      syncService.syncAfterLogin(user),

    invalidateUserCache: (userId: string | number) =>
      syncService.invalidateUserCache(userId),
  };
};

export default UserSyncService;
